import React from "react";
import Input from "../Input";
import { useEffect, useRef, useState, useCallback } from "react";
import ToasterService from "utils/toaster.util";
import { AutoCompleteOwnProps } from ".";
import debounce from "lodash.debounce";
import {
  FaHome,
  FaBuilding,
  FaMapMarkerAlt,
  FaHotel,
  FaPlane,
  FaStore,
  FaRoad,
  FaLandmark,
  FaUtensils,
} from "react-icons/fa";

import { useTranslation } from "react-i18next";
import {
  Box,
  Drawer,
  IconButton,
  Popper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import clear from "../../../assets/icons/clear.svg";
import mark from "../../../assets/icons/mark.svg";
import map from "../../../assets/icons/map.svg";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { mapAction } from "redux/slices/map/mapSlice";
import BookingService from "services/booking.service";
import MapsLoaderService from "services/map.loader.service";
import { useScrollLock } from "hooks/useScrollLock";
import "./autocomplete.css";

export interface ILocation {
  name?: string;
  address: string;
  title?: string;
  place_id?: string;
  city?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  country_name_code: string;
  types?: string[];
}

const typeIconMap: Record<string, any> = {
  home: FaHome,
  premise: FaBuilding,
  point_of_interest: FaLandmark,
  street_address: FaRoad,
  route: FaRoad,
  lodging: FaHotel,
  airport: FaPlane,
  food: FaUtensils,
  store: FaStore,
  establishment: FaBuilding,
  city: FaBuilding,
  locality: FaBuilding,
  default: FaMapMarkerAlt,
};

function placesAutocompleteReady(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean(window.google?.maps?.places) &&
    typeof window.google.maps.places.AutocompleteSuggestion
      ?.fetchAutocompleteSuggestions === "function"
  );
}

function latLngToLiteral(loc: google.maps.LatLng | google.maps.LatLngLiteral) {
  const l = loc as google.maps.LatLng;
  if (typeof l.lat === "function") {
    return { lat: l.lat(), lng: l.lng() };
  }
  const lit = loc as google.maps.LatLngLiteral;
  return { lat: lit.lat, lng: lit.lng };
}

export type AutoCompletesProps = AutoCompleteOwnProps &
  React.ComponentProps<typeof Input> & {
    disableInitialDropdown?: boolean;
    renderDrawerContentOnly?: boolean;
    drawerInputAutoFocus?: boolean;
    mobileTriggerProps?: any;
  };

export default function AutoCompleteApi({
  val,
  value,
  handleChange,
  onSelectionComplete,
  searchForm,
  disableInitialDropdown = false,
  renderDrawerContentOnly = false,
  drawerInputAutoFocus = false,
  mobileTriggerProps,
  ...rest
}: AutoCompletesProps) {
  const { t, i18n } = useTranslation();
  const ref = useRef<any>(null);
  const drawerInputRef = useRef<HTMLInputElement | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const fromMap = useAppSelector((state) => state.map.close);
  const mapIsOpen = useAppSelector((state) => state.map.open);

  const [inputValue, setInputValue] = useState(val?.value || "");
  const [drawerQuery, setDrawerQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<ILocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState("");
  const [showMap] = useState(false);
  const [isEditingExistingValue, setIsEditingExistingValue] =
    useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [userExplicitlyCleared, setUserExplicitlyCleared] =
    useState<boolean>(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const predictionByPlaceIdRef = useRef<
    Map<string, google.maps.places.PlacePrediction>
  >(new Map());
  const isSelecting = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currentSearchValue =
    renderDrawerContentOnly && drawerQuery.length === 0
      ? inputValue
      : renderDrawerContentOnly
        ? drawerQuery
        : inputValue;
  const activeQueryValue = renderDrawerContentOnly ? drawerQuery : inputValue;
  const hasSelectedValue = Boolean(inputValue);
  const showCurrentLocationAndMap =
    isFocused &&
    hasSelectedValue &&
    !isEditingExistingValue &&
    (!renderDrawerContentOnly || !activeQueryValue.trim()) &&
    !disableInitialDropdown;
  const shouldShowSuggestions =
    showSuggestions && suggestions.length > 0 && isFocused;
  const showEmptyFieldOptions =
    isFocused &&
    !showMap &&
    !hasSelectedValue &&
    (!activeQueryValue || userExplicitlyCleared) &&
    !disableInitialDropdown;

  const defaultVal = useRef(false);
  const isDestinationField = rest.name === "destination";

  useScrollLock(
    !isMobile &&
      isFocused &&
      (shouldShowSuggestions ||
        showEmptyFieldOptions ||
        showCurrentLocationAndMap),
  );

  useEffect(() => {
    MapsLoaderService.LoadScripts();

    const initializeServices = () => {
      if (placesAutocompleteReady()) {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current =
            new window.google.maps.places.AutocompleteSessionToken();
        }
        return true;
      }
      return false;
    };

    if (!initializeServices()) {
      const checkInterval = setInterval(() => {
        if (initializeServices()) clearInterval(checkInterval);
      }, 100);

      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        if (!placesAutocompleteReady()) {
          console.error(
            "Google Maps Places (AutocompleteSuggestion) failed to load",
          );
        }
      }, 10000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        initializeServices();
        isSelecting.current = false;
        if (document.activeElement === ref.current) {
          setIsFocused(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [i18n.language]);

  useEffect(() => {
    const nextValue = val?.value || "";
    setInputValue(nextValue);
    if (renderDrawerContentOnly) {
      setDrawerQuery("");
    }
    if (!nextValue) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsEditingExistingValue(false);
      setUserExplicitlyCleared(false);
    }
  }, [val?.value]);

  const syncSelection = useCallback(
    (nextValue: string, details: any) => {
      handleChange?.({ value: nextValue, details });

      if (!details) {
        if (isDestinationField) {
          dispatch(mapAction.setEndFormatted(null));
          dispatch(mapAction.setEndLocation({}));
        } else {
          dispatch(mapAction.setStartFormatted(null));
          dispatch(mapAction.setStartLocation({}));
        }
        return;
      }

      if (isDestinationField) {
        dispatch(mapAction.setEndLocation(details.geometry?.location || {}));
        dispatch(mapAction.setEndFormatted(details));
      } else {
        dispatch(mapAction.setStartLocation(details.geometry?.location || {}));
        dispatch(mapAction.setStartFormatted(details));
      }
    },
    [dispatch, handleChange, isDestinationField],
  );

  const finishSelection = useCallback(
    (selectedValue?: any) => {
      setShowSuggestions(false);
      setIsFocused(false);
      setSelectedIndex(-1);
      setLoading(false);
      isSelecting.current = false;
      setIsMobileDrawerOpen(false);

      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      ref.current?.blur?.();
      (document.activeElement as HTMLElement | null)?.blur?.();
      onSelectionComplete?.(selectedValue);
    },
    [onSelectionComplete],
  );

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      isSelecting.current = false;
      ref.current?.blur?.();
      (document.activeElement as HTMLElement | null)?.blur?.();
    };
  }, []);

  // ── Mobile drawer handlers ──────────────────────────────────────────────────

  const handleMobileDrawerOpen = useCallback((e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    (e?.target as HTMLElement | null)?.blur();
      ref.current?.blur();
      setIsMobileDrawerOpen(true);
      setIsFocused(true);
      setDrawerQuery("");
      setIsEditingExistingValue(false);
      isSelecting.current = false;
  }, []);

  const handleMobileDrawerClose = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    setIsMobileDrawerOpen(false);
    setIsFocused(false);
    setShowSuggestions(false);
    setDrawerQuery("");
    setIsEditingExistingValue(false);
    setTimeout(() => {
      (document.activeElement as HTMLElement | null)?.blur();
    }, 50);
  }, []);

  // ── Place fetching ──────────────────────────────────────────────────────────

  const fetchPlaceDetails = async (placeId: string) => {
    if (!placesAutocompleteReady()) {
      ToasterService.showError("Google Places service not available");
      return;
    }

    const prediction = predictionByPlaceIdRef.current.get(placeId);
    const place = prediction
      ? prediction.toPlace()
      : new window.google.maps.places.Place({
          id: placeId,
          requestedLanguage: i18n.language,
        });

    try {
      await place.fetchFields({
        fields: [
          "id",
          "displayName",
          "formattedAddress",
          "location",
          "addressComponents",
          "types",
        ],
      });
    } catch {
      ToasterService.showError("Failed to load place details.");
      return;
    }

    if (!place.location) {
      ToasterService.showError(
        `No details available for input: '${place.displayName ?? ""}'`,
      );
      return;
    }

    const { lat, lng } = latLngToLiteral(place.location);
    const geometry = { location: { lat, lng } };

    let airport = false;
    let city: any;
    let route: any;
    let country_name_code = "";
    const components = place.addressComponents ?? [];

    for (const c of components) {
      if (c.types.includes("country")) {
        country_name_code = c.shortText ?? "";
      }
    }
    components.every((c) => {
      if (c.types.includes("route")) route = c.longText;
      if (
        c.types.includes("locality") ||
        c.types.includes("administrative_area_level_1")
      ) {
        city = c.longText;
      }
      if (route && city) return false;
      return true;
    });

    const types = place.types ?? [];
    if (types.includes("airport")) airport = true;
    else if (
      place.displayName &&
      place.displayName.toLowerCase().includes("airport")
    ) {
      airport = true;
    }

    if (city === "Berlin") {
      if (!city) ToasterService.showError("Address does not contain city!");
      if (!route) ToasterService.showError("Address does not contain route!");
      if (types.includes("route"))
        ToasterService.showError("Address does not contain street number!");
    }

    const displayName = place.displayName ?? "";
    const formattedAddress = place.formattedAddress ?? "";

    const details = {
      city,
      route,
      airport,
      geometry,
      country_name_code,
      name: displayName,
      address: formattedAddress,
      title: displayName,
      formatted_address: formattedAddress,
      place_id: place.id,
      coordinates: { lat, lng },
      types,
      address_components: components.map((c) => ({
        types: c.types,
        long_name: c.longText ?? "",
        short_name: c.shortText ?? "",
      })),
    };

    isSelecting.current = true;
    setInputValue(displayName || formattedAddress);
    setDrawerQuery("");
    setShowSuggestions(false);
    setSuggestions([]);
    setIsEditingExistingValue(false);

    syncSelection(formattedAddress, details);
    finishSelection(details);

    if (user) {
      BookingService.requestHistory({ ...details, user });
    }

    sessionTokenRef.current =
      new window.google.maps.places.AutocompleteSessionToken();
    predictionByPlaceIdRef.current.clear();
  };

  const fetchLocations = useCallback(
    debounce(async (query: string) => {
      if (!query) {
        setSuggestions([]);
        return;
      }

      if (!placesAutocompleteReady()) {
        console.warn(
          "Google Maps Places (AutocompleteSuggestion) not yet loaded",
        );
        setSuggestions([]);
        setLoading(false);
        return;
      }

      if (!sessionTokenRef.current) {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }

      setLoading(true);
      try {
        const { suggestions: rawSuggestions } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              input: query,
              language: i18n.language,
              sessionToken: sessionTokenRef.current,
            },
          );

        predictionByPlaceIdRef.current.clear();
        const locations: ILocation[] = [];
        for (const s of rawSuggestions) {
          const pp = s.placePrediction;
          if (!pp) continue;
          predictionByPlaceIdRef.current.set(pp.placeId, pp);
          const main = pp.mainText?.text ?? "";
          const fullText = pp.text.text;
          locations.push({
            name: main || fullText,
            address: fullText,
            title: main || fullText,
            place_id: pp.placeId,
            coordinates: { lat: 0, lng: 0 },
            country_name_code: "",
            types: pp.types,
          });
        }
        setSuggestions(locations);
        setShowSuggestions(locations.length > 0);
      } catch {
        ToasterService.showError("Failed to fetch locations");
        setSuggestions([]);
      }
      setLoading(false);
    }, 300),
    [i18n.language],
  );

  useEffect(() => {
    if (isSelecting.current) return;

    const trimmedInput = activeQueryValue.trim();

    if (trimmedInput.length > 2 && !fromMap && isFocused) {
      if (activeQueryValue !== val?.value && trimmedInput !== lastSearchTerm) {
        fetchLocations(activeQueryValue);
        setLastSearchTerm(trimmedInput);
        setShowSuggestions(true);
      }
    } else if (trimmedInput.length <= 2) {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    if (!activeQueryValue) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLastSearchTerm("");
      dispatch(mapAction.setClose(false));
    }
  }, [activeQueryValue, fetchLocations, lastSearchTerm, val?.value, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (renderDrawerContentOnly) {
      setDrawerQuery(newValue);
    } else {
      setInputValue(newValue);
    }

    if (
      val?.details &&
      newValue !== (val.details?.title || val.details?.name || val.value)
    ) {
      setIsEditingExistingValue(true);
    }

    if (newValue.trim() === "") {
      if (!isSelecting.current && activeQueryValue.length > 0) {
        setUserExplicitlyCleared(true);
      }
      if (renderDrawerContentOnly) {
        setIsEditingExistingValue(false);
        setShowSuggestions(false);
        setSelectedIndex(-1);
      } else {
        setIsEditingExistingValue(false);
        syncSelection("", null);
      }
    } else {
      setUserExplicitlyCleared(false);
    }

    isSelecting.current = false;
  };

  const handleSuggestionClick = (location: ILocation) => {
    if (location.place_id) {
      void fetchPlaceDetails(location.place_id);
    } else {
      isSelecting.current = true;
      setInputValue(location.title || location.address);
      setDrawerQuery("");
      setShowSuggestions(false);

      const details = {
        ...location,
        name: location.name || location.title,
        city: location.city,
        airport: location?.types?.includes("airport") ? true : false,
        formatted_address: location.address,
        geometry: {
          location: {
            lat: location.coordinates.lat,
            lng: location.coordinates.lng,
          },
        },
      };

      syncSelection(location.address, details);
      finishSelection(details);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (currentSearchValue === "") {
        setUserExplicitlyCleared(true);
        if (!isFocused) setIsFocused(true);
      }
      isSelecting.current = false;
    }
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        e.preventDefault();
        handleSuggestionClick(suggestions[selectedIndex]);
        setShowSuggestions(false);
      }
    }
  };

  const getTypeIcon = (types?: string[]) => {
    if (!types || types.length === 0) return typeIconMap.default;
    for (const type of types) {
      if (typeIconMap[type]) return typeIconMap[type];
    }
    return typeIconMap.default;
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
      setIsFocused(false);
    }, 200);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    // User clicked/tapped the input while the map was open — close the map so
    // suggestions become visible without overlapping (desktop) or so the input
    // expands back to full height (mobile renderDrawerContentOnly).
    if (mapIsOpen) {
      dispatch(mapAction.setOpen(false));
    }

    setIsFocused(true);
    isSelecting.current = false;

    if (currentSearchValue === "" && userExplicitlyCleared) {
      setShowSuggestions(false);
    }
  };

  const handleUseEffectfocus = () => {
    setIsFocused(true);
    isSelecting.current = false;
  };

  useEffect(() => {
    const inputElement = ref.current;
    if (inputElement) {
      const handleFocusEvent = () => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        if (!isFocused) {
          setIsFocused(true);
          isSelecting.current = false;
        }
      };

      inputElement.addEventListener(
        "focus",
        handleFocusEvent,
        handleUseEffectfocus,
      );
      inputElement.addEventListener("blur", handleBlur);

      return () => {
        inputElement.removeEventListener(
          "focus",
          handleFocusEvent,
          handleUseEffectfocus,
        );
        inputElement.removeEventListener("blur", handleBlur);
        if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      };
    }
  }, [isFocused]);

  const getCurrentLocation = async (type: "pickup" | "destination") => {
    if (isMobileDrawerOpen) handleMobileDrawerClose();
    try {
      if (!navigator.geolocation) {
        ToasterService.showError(
          "Geolocation is not supported by this browser",
        );
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          dispatch(
            mapAction.setStartLocation({ lat: latitude, lng: longitude }),
          );
          dispatch(mapAction.setOpenedFromCurrentLocation(true));

          if (type === "destination") {
            dispatch(mapAction.setShow(1));
          } else {
            dispatch(mapAction.setShow(0));
          }
          dispatch(mapAction.setOpen(true));
        },
        (error) => {
          ToasterService.showError("Unable to get your location");
          console.error("Geolocation error:", error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
      );
    } catch (error) {
      ToasterService.showError("Error getting location");
      console.error("Location error:", error);
    }
  };

  const handleChooseFromMap = (
    e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    isSelecting.current = true;

    if (isMobileDrawerOpen) handleMobileDrawerClose();

    dispatch(mapAction.setStartLocation({}));
    dispatch(mapAction.setOpenedFromCurrentLocation(false));

    if (isDestinationField) {
      dispatch(mapAction.setShow(1));
    } else {
      dispatch(mapAction.setShow(0));
    }

    dispatch(mapAction.setOpen(true));
    setIsFocused(false);
    setShowSuggestions(false);

    if (ref.current) ref.current.blur();
    isSelecting.current = false;
  };

  const handleClear = (
    e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    isSelecting.current = false;
    predictionByPlaceIdRef.current.clear();
    if (placesAutocompleteReady()) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }

    setInputValue("");
    setDrawerQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    defaultVal.current = false;
    setUserExplicitlyCleared(true);
    setLastSearchTerm("");
    syncSelection("", null);

    if (isMobileDrawerOpen || renderDrawerContentOnly) {
      setIsFocused(true);
      setTimeout(() => drawerInputRef.current?.focus(), 10);
    } else {
      setIsFocused(false);
      if (ref.current) ref.current.blur();
    }
  };

  // ── Shared suggestion list item renderer ─────────────────────────────────

  const renderSuggestionItem = (location: ILocation, idx: number) => (
    <Box
      key={location.place_id || idx}
      bgcolor={idx === selectedIndex ? "#F2F2F7 !important" : ""}
      onClick={() => handleSuggestionClick(location)}
      px={"8px"}
      sx={{
        cursor: "pointer",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        ":hover": { backgroundColor: "#f9f9f9", borderRadius: "16px" },
      }}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        gap={"12px"}
        sx={{ minWidth: 0, width: "100%", maxWidth: "100%", overflow: "hidden" }}
      >
        <Stack
          p={"6px"}
          borderRadius={"8px"}
          sx={{ color: "black", flexShrink: 0 }} // add flexShrink: 0
        >
          {React.createElement(getTypeIcon(location.types), {
            size: 16,
            color: "#00000099",
          })}
        </Stack>
        <Box width={"100%"} minWidth={0} maxWidth={"100%"} p="12px 0" overflow={"hidden"}>
          <Typography
            component={"p"}
            fontSize={"16px"}
            color={"#000000ff"}
            fontWeight={500}
            overflow={"hidden"}
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
            maxWidth={{ xs: "85%", md: "380px" }}
            lineHeight={"normal"}
          >
            {location.title}
          </Typography>
          {location.title && (
            <Typography
              component={"p"}
              color={"#00000099"}
              fontSize={"13.67px"}
              fontWeight={500}
              whiteSpace={"normal"}
              lineHeight={"normal"}
              maxWidth={{ xs: "90%", md: "380px" }}
            >
              {location.address}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );

  // ── Shared location options (current location + map) ─────────────────────

  const renderLocationOptions = () => (
    <>
      {val?.details?.name && (
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
              blurTimeoutRef.current = null;
            }
            finishSelection(val?.details);
          }}
          sx={{
            cursor: "pointer",
            py: 2,
            px: 1.5,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflowX: "hidden",
            ":hover": { backgroundColor: "#F2F2F7", borderRadius: "12px" },
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"12px"}
            sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}
          >
            <Stack p={"6px"} borderRadius={"8px"} sx={{ flexShrink: 0 }}>
              {React.createElement(getTypeIcon(val?.details?.types), {
                size: 16,
                color: "#00000099",
              })}
            </Stack>
            <Box overflow={"hidden"} flex={1}>
              <Typography
                component={"p"}
                fontSize={"16px"}
                color={"#000000ff"}
                fontWeight={500}
                overflow={"hidden"}
                textOverflow={"ellipsis"}
                whiteSpace={"nowrap"}
                lineHeight={"normal"}
              >
                {val?.details?.name || val?.details?.title}
              </Typography>
              <Typography
                component={"p"}
                color={"#00000099"}
                fontSize={"13.67px"}
                fontWeight={500}
                overflow={"hidden"}
                textOverflow={"ellipsis"}
                whiteSpace={"nowrap"}
                lineHeight={"normal"}
              >
                {val?.details?.formatted_address || val?.details?.address}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}

      <Box
        onClick={() =>
          getCurrentLocation(isDestinationField ? "destination" : "pickup")
        }
        sx={{
          cursor: "pointer",
          p: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          ":hover": { backgroundColor: "#F2F2F7", borderRadius: "12px" },
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={"12px"}
          sx={{ minWidth: 0, width: "100%", maxWidth: "100%", overflow: "hidden" }}
        >
          <Stack sx={{ flexShrink: 0 }}>
            <img src={mark} alt="mark" />
          </Stack>
          <Typography
            component={"p"}
            fontSize={"16px"}
            color={"#000000ff"}
            fontWeight={500}
          >
            {t("current_location")}
          </Typography>
        </Stack>
      </Box>

      <Box
        onClick={handleChooseFromMap}
        sx={{
          cursor: "pointer",
          p: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          ":hover": { backgroundColor: "#F2F2F7", borderRadius: "12px" },
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={"12px"}
          sx={{ minWidth: 0, width: "100%", maxWidth: "100%", overflow: "hidden" }}
        >
          <Stack sx={{ flexShrink: 0 }}>
            <img src={map} alt="map" />
          </Stack>
          <Typography
            component={"p"}
            fontSize={"16px"}
            color={"#000000ff"}
            fontWeight={500}
          >
            {t("choose_from_map")}
          </Typography>
        </Stack>
      </Box>
    </>
  );

  const renderEmptyOptions = () => (
    <>
      <Box
        onClick={() =>
          getCurrentLocation(isDestinationField ? "destination" : "pickup")
        }
        sx={{
          cursor: "pointer",
          p: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          ":hover": { backgroundColor: "#F2F2F7", borderRadius: "12px" },
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={"12px"}
          sx={{ minWidth: 0, width: "100%", maxWidth: "100%", overflow: "hidden" }}
        >
          <Stack sx={{ flexShrink: 0 }}>
            <img src={mark} alt="mark" />
          </Stack>
          <Typography
            component={"p"}
            fontSize={"16px"}
            color={"#000000ff"}
            fontWeight={500}
          >
            {t("current_location")}
          </Typography>
        </Stack>
      </Box>

      <Box
        onClick={handleChooseFromMap}
        sx={{
          cursor: "pointer",
          p: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          ":hover": { backgroundColor: "#F2F2F7", borderRadius: "12px" },
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={"12px"}
          sx={{ minWidth: 0, width: "100%", maxWidth: "100%", overflow: "hidden" }}
        >
          <Stack sx={{ flexShrink: 0 }}>
            <img src={map} alt="map" />
          </Stack>
          <Typography
            component={"p"}
            fontSize={"16px"}
            color={"#000000ff"}
            fontWeight={500}
          >
            {t("choose_from_map")}
          </Typography>
        </Stack>
      </Box>
    </>
  );

  // ── Skeleton ──────────────────────────────────────────────────────────────

  const renderSkeletonItems = () =>
    [...Array(4)].map((_, idx) => (
      <Box
        key={idx}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "12px 8px",
          marginBottom: "8px",
        }}
      >
        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
        <Box sx={{ width: "60%" }}>
          <Skeleton variant="text" width="100%" height={14} />
          <Skeleton variant="text" width="80%" height={12} sx={{ mt: 1 }} />
        </Box>
      </Box>
    ));

  const renderDrawerSearchContent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "#fff",
          borderBottom: "1px solid #f0f0f0",
          px: 2,
          py: 1.5,
          flexShrink: 0,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Box
              component="input"
              ref={drawerInputRef}
              autoFocus={drawerInputAutoFocus}
              value={currentSearchValue}
              onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                handleFocus();
                const resetVisibleText = () => {
                  try {
                    if (renderDrawerContentOnly && drawerQuery.length === 0 && inputValue) {
                      e.currentTarget.setSelectionRange(0, inputValue.length);
                    } else {
                      e.currentTarget.setSelectionRange(0, 0);
                    }
                    e.currentTarget.scrollLeft = 0;
                  } catch {
                    // Some mobile browsers may block selection APIs briefly.
                  }
                };
                window.requestAnimationFrame(resetVisibleText);
                window.setTimeout(resetVisibleText, 30);
              }}
              onChange={handleInputChange}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                handleKeyDown(e);
                if (e.key === "Enter") e.preventDefault();
              }}
              placeholder={t("enterLocation") || "Enter Location"}
              sx={{
                display: "block",
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                boxSizing: "border-box",
                WebkitAppearance: "none",
                appearance: "none",
                border: "none",
                outline: "none",
                fontSize: "16px",
                fontWeight: 500,
                color: "#0A0A0E",
                bgcolor: "transparent",
                fontFamily: "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                pr: currentSearchValue ? "44px" : 0,
                "::placeholder": { color: "#00000066" },
              }}
            />
            {currentSearchValue && (
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClear}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  width: 32,
                  height: 32,
                  p: 0,
                  bgcolor: "#fff",
                  flexShrink: 0,
                }}
              >
                <img
                  src={clear}
                  alt="clear"
                  style={{ width: 18, height: 18 }}
                />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          maxHeight: "45dvh",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
        data-scroll-lock-allow="true"
      >
        {loading && <Box sx={{ p: 2 }}>{renderSkeletonItems()}</Box>}
        {!loading && shouldShowSuggestions && (
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            {suggestions.map((location, idx) =>
              renderSuggestionItem(location, idx),
            )}
          </Box>
        )}

        {!loading && !shouldShowSuggestions && showCurrentLocationAndMap && (
          <Box sx={{ p: 2, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", overflowX: "hidden" }}>
            {renderLocationOptions()}
          </Box>
        )}
        {!loading && !shouldShowSuggestions && showEmptyFieldOptions && (
          <Box sx={{ p: 2, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", overflowX: "hidden" }}>
            {renderEmptyOptions()}
          </Box>
        )}
      </Box>
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  if (renderDrawerContentOnly) {
    return renderDrawerSearchContent();
  }

  const pageFieldTriggerProps = !searchForm ? mobileTriggerProps : undefined;
  const shouldShowInlineClearButton =
    !pageFieldTriggerProps && isFocused && inputValue.length > 0;

  return (
    <>
      <div style={{ position: "relative" }}>
        <Input
          inputRef={ref}
          placeholder={t("enterLocation") || "Enter Location"}
          {...rest}
          customLabelStyles={true}
          value={inputValue}
          onBlur={isMobile && searchForm ? undefined : handleBlur}
          onFocusCapture={(e: React.FocusEvent) => {
            if (pageFieldTriggerProps?.onFocusCapture) {
              pageFieldTriggerProps.onFocusCapture(e);
            } else if (isMobile && searchForm) {
              e.preventDefault();
              (e.target as HTMLElement).blur();
              handleMobileDrawerOpen(e);
            } else {
              handleFocus();
            }
          }}
          onMouseDown={pageFieldTriggerProps?.onMouseDown}
          onClick={
            pageFieldTriggerProps?.onClick ||
            (isMobile && searchForm ? handleMobileDrawerOpen : undefined)
          }
          onKeyDown={
            pageFieldTriggerProps?.onKeyDown ||
            (isMobile && searchForm
              ? undefined
              : (e) => {
                  handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
                  rest.onKeyDown?.(e);
                  if (e.key === "Enter") e.preventDefault();
                })
          }
          label={val?.details?.name || rest.label}
          onChange={isMobile && searchForm ? undefined : handleInputChange}
          autoComplete="off"
          InputProps={{
            ...rest.InputProps,
            endAdornment: (
              <>
                {rest.InputProps && rest.InputProps.endAdornment}
                {shouldShowInlineClearButton && (
                  <button
                    type="button"
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      marginRight: 4,
                      position: "absolute",
                      right: i18n.language === "en" ? 0 : "",
                      left: i18n.language === "ar" ? 0 : "",
                      top: -20,
                      bottom: 0,
                      zIndex: 99999999,
                      border: "none",
                      margin: "auto",
                      background: "none",
                    }}
                    onClick={(e) => handleClear(e)}
                    title="Clear"
                  >
                    <img
                      src={clear}
                      alt="clear"
                      style={{ height: "17.96px", width: "17.96px" }}
                    />
                  </button>
                )}
              </>
            ),
          }}
          inputProps={{
            ...rest.inputProps,
            ...pageFieldTriggerProps?.inputProps,
            ...(isMobile && searchForm
              ? {
                  readOnly: true,
                  inputMode: "none" as const,
                }
              : {}),
            style: {
              // ← ADD THIS
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />

        {/* ── Desktop: current location + map options ── */}
        {!showSuggestions && (
          <Popper
            open={!isMobile && showCurrentLocationAndMap}
            anchorEl={ref.current}
            placement="bottom-start"
            style={{ zIndex: 1500 }}
            modifiers={[{ name: "offset", options: { offset: [-6, 10] } }]}
          >
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "16px",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.15)",
                p: 2,
                width: ref.current?.offsetWidth - 15 || 300,
              }}
            >
              {renderLocationOptions()}
            </Box>
          </Popper>
        )}

        {/* ── Desktop: empty field options ── */}
        <Popper
          open={!isMobile && showEmptyFieldOptions}
          anchorEl={ref.current}
          placement="bottom-start"
          style={{ zIndex: 1500 }}
          modifiers={[{ name: "offset", options: { offset: [-6, 10] } }]}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "16px",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.15)",
              p: 2,
              width: ref.current?.offsetWidth - 15 || 300,
              maxHeight: 200,
              overflowY: "auto",
            }}
            data-scroll-lock-allow="true"
          >
            {renderEmptyOptions()}
          </Box>
        </Popper>

        {/* ── Suggestions: mobile non-searchForm inline / desktop Popper ── */}
        {isMobile && !searchForm ? (
          <Box mt={2}>
            {isFocused && shouldShowSuggestions && (
              <Box
                position="absolute"
                zIndex={9999}
                width="100%"
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.15)",
                  maxHeight: 270,
                  overflowY: "auto",
                }}
                data-scroll-lock-allow="true"
              >
                {suggestions.map((location, idx) =>
                  renderSuggestionItem(location, idx),
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Popper
            open={
              !showEmptyFieldOptions &&
              !isMobile &&
              shouldShowSuggestions &&
              suggestions.length > 0 &&
              !loading
            }
            anchorEl={ref.current}
            placement="bottom-start"
            modifiers={[{ name: "offset", options: { offset: [-6, 10] } }]}
            style={{ zIndex: 99999 }}
          >
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "16px",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.15)",
                maxHeight: 350,
                overflowY: "auto",
                width: ref.current?.offsetWidth + 20 || 420,
              }}
              data-scroll-lock-allow="true"
            >
              {suggestions.map((location, idx) =>
                renderSuggestionItem(location, idx),
              )}
            </Box>
          </Popper>
        )}

        {/* ── Desktop skeleton loader ── */}
        <Popper
          open={loading && !isMobile}
          anchorEl={ref.current}
          placement="bottom-start"
          style={{ zIndex: 1500 }}
        >
          <Box
            sx={{
              padding: 2,
              bgcolor: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.15)",
              maxHeight: 350,
              mt: 2,
              overflowY: "auto",
              width: ref.current?.offsetWidth - 16 || 420,
            }}
            data-scroll-lock-allow="true"
          >
            {renderSkeletonItems()}
          </Box>
        </Popper>
      </div>

      {/* ── Mobile full-screen bottom drawer (searchForm only) ── */}
      {isMobile && searchForm && (
        <Drawer
          anchor="bottom"
          open={isMobileDrawerOpen}
          onClose={handleMobileDrawerClose}
          ModalProps={{
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableRestoreFocus: true,
          }}
          PaperProps={{
            sx: {
              height: "100dvh",
              maxHeight: "100dvh",
              overflow: "hidden",
              overflowX: "hidden", // ← ADD THIS

              display: "flex",
              flexDirection: "column",
              borderRadius: 0,
            },
          }}
          sx={{ zIndex: 1500 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: "#fff",
                borderBottom: "1px solid #f0f0f0",
                px: 2,
                py: 1.5,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  onClick={handleMobileDrawerClose}
                  size="small"
                  sx={{ color: "#0A0A0E" }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box
                  component="input"
                  ref={drawerInputRef}
                  autoFocus
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    handleKeyDown(e);
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  placeholder={t("enterLocation") || "Enter Location"}
                  sx={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#0A0A0E",
                    bgcolor: "transparent",
                    fontFamily: "inherit",
                    "::placeholder": { color: "#00000066" },
                  }}
                />
                {inputValue && (
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleClear}
                  >
                    <img
                      src={clear}
                      alt="clear"
                      style={{ width: 18, height: 18 }}
                    />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Box
              sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}
              data-scroll-lock-allow="true"
            >
              {loading && <Box sx={{ p: 2 }}>{renderSkeletonItems()}</Box>}
              {!loading && shouldShowSuggestions && (
                <Box>
                  {suggestions.map((location, idx) =>
                    renderSuggestionItem(location, idx),
                  )}
                </Box>
              )}
              {!loading &&
                !shouldShowSuggestions &&
                showCurrentLocationAndMap && (
                  <Box sx={{ p: 2 }}>{renderLocationOptions()}</Box>
                )}
              {!loading && !shouldShowSuggestions && showEmptyFieldOptions && (
                <Box sx={{ p: 2 }}>{renderEmptyOptions()}</Box>
              )}
            </Box>
          </Box>
        </Drawer>
      )}
    </>
  );
}
